// Card faces for phones. They show the same card pictures as desktop, but a
// picture's printed corner number shrinks to a few pixels on a phone, so a
// crisp, larger rank and suit is drawn over it: across the middle of a board
// space (the picture faded behind it), and over the corner of a hand card.

export const cardArt = (card) => `https://d5hzzxzqjp9y1.cloudfront.net/cardfronts/${card}.png`;

const SUITS = {
    hearts: { red: true, path: 'M12 21.2s-7.6-4.7-9.7-9.4C.8 8.4 2.9 4.4 6.7 4.4c2.2 0 3.7 1.2 5.3 3.3 1.6-2.1 3.1-3.3 5.3-3.3 3.8 0 5.9 4 4.4 7.4-2.1 4.7-9.7 9.4-9.7 9.4z' },
    diamonds: { red: true, path: 'M12 2.2c2.3 3.4 5 6.6 8.2 9.8-3.2 3.2-5.9 6.4-8.2 9.8-2.3-3.4-5-6.6-8.2-9.8 3.2-3.2 5.9-6.4 8.2-9.8z' },
    spades: { red: false, path: 'M12 2.2C9.4 5.9 3.4 9.1 3.4 13.5c0 2.6 2 4.6 4.5 4.6 1.4 0 2.5-.5 3.3-1.5-.2 2-1 3.6-2.5 5h6.6c-1.5-1.4-2.3-3-2.5-5 .8 1 1.9 1.5 3.3 1.5 2.5 0 4.5-2 4.5-4.6 0-4.4-6-7.6-8.6-11.3z' },
    clubs: { red: false, circles: [[12, 7.2], [7.1, 13.6], [16.9, 13.6]], path: 'M10.9 9.6h2.2l.9 6.6c.4 2.1 1.2 3.7 2.6 5.2H7.4c1.4-1.5 2.2-3.1 2.6-5.2z' },
};

const RANKS = { ace: 'A', king: 'K', queen: 'Q', jack: 'J' };

// Spades and hearts jacks are drawn in profile: one eye.
const ONE_EYED = ['spades_jack', 'hearts_jack'];

export const parseCard = (card) => {
    if (!card || card === 'blank_card') {
        return { corner: true };
    }
    const [suit, value] = String(card).split('_');
    const rank = RANKS[value] || value;
    const jack = value === 'jack' ? (ONE_EYED.includes(card) ? 'remove' : 'wild') : null;
    return { suit, rank, red: Boolean(SUITS[suit]?.red), jack };
};

export const Suit = ({ suit, className }) => {
    const shape = SUITS[suit];
    if (!shape) {
        return null;
    }
    return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            {shape.circles && shape.circles.map(([cx, cy]) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="4.5"/>)}
            <path d={shape.path}/>
        </svg>
    );
};

/** One space on the phone board: the card picture, faded, under a readable rank and suit. */
export const BoardFace = ({ card }) => {
    const face = parseCard(card);
    return (
        <span className={`face${face.corner ? ' corner-face' : ''}${face.red ? ' red' : ''}${['K', 'Q'].includes(face.rank) ? ' court' : ''}`} aria-hidden="true">
            <img className="face-art" src={cardArt(face.corner ? 'blank_card' : card)} alt="" draggable={false}/>
            {face.corner
                ? <span className="corner-star">★</span>
                : <span className="face-index">
                    <span className="face-rank">{face.rank}</span>
                    <Suit suit={face.suit} className="face-suit"/>
                </span>}
        </span>
    );
};

/** One card in the phone hand: the picture stood upright, a readable index, and for jacks what they do. */
export const HandFace = ({ card }) => {
    const face = parseCard(card);
    return (
        <span className={`hand-face${face.red ? ' red' : ''}${face.jack ? ` jack ${face.jack}` : ''}`} aria-hidden="true">
            <img className="hand-face-art" src={cardArt(card)} alt="" draggable={false}/>
            <span className="hand-face-index">
                <span className="hand-face-rank">{face.rank}</span>
                <Suit suit={face.suit} className="hand-face-pip"/>
            </span>
            {face.jack && <span className="hand-face-tag">{face.jack === 'remove' ? 'Remove' : 'Wild'}</span>}
        </span>
    );
};
